import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import AuthDialog from "@/components/AuthDialog";
import Index from "./pages/Index";

const DBPro = lazy(() => import("./pages/DBPro"));
const Consultation = lazy(() => import("./pages/Consultation"));
const Bootcamp = lazy(() => import("./pages/Bootcamp"));
const BootcampRegistration = lazy(() => import("./pages/BootcampRegistration"));
const BootcampWaitlist = lazy(() => import("./pages/BootcampWaitlist"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail"));
const Wireframe = lazy(() => import("./pages/Wireframe"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancelled = lazy(() => import("./pages/PaymentCancelled"));
const BookingSuccess = lazy(() => import("./pages/BookingSuccess"));
const KhalidProfile = lazy(() => import("./pages/KhalidProfile"));
const NotFound = lazy(() => import("./pages/NotFound"));
const BootcampSuccess = lazy(() => import("./pages/BootcampSuccess"));
const DBProSuccess = lazy(() => import("./pages/DBProSuccess"));
const Blog = lazy(() => import("./pages/Blog"));
const Shop = lazy(() => import("./pages/Shop"));
const ShopProduct = lazy(() => import("./pages/ShopProduct"));
const Careers = lazy(() => import("./pages/Careers"));
const Team = lazy(() => import("./pages/Team"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Advertise = lazy(() => import("./pages/Advertise"));
const Events = lazy(() => import("./pages/Events"));
const Vendors = lazy(() => import("./pages/Vendors"));
const Testimonial = lazy(() => import("./pages/Testimonial"));
const TestimonialSuccess = lazy(() => import("./pages/TestimonialSuccess"));
const InfluencerCampaign = lazy(() => import("./pages/InfluencerCampaign"));
const Influencers = lazy(() => import("./pages/Influencers"));
const InfluencerNetwork = lazy(() => import("./pages/InfluencerNetwork"));
const Services = lazy(() => import("./pages/Services"));

const WorkWithUs = lazy(() => import("./pages/WorkWithUs"));
const WorkWithUsSuccess = lazy(() => import("./pages/WorkWithUsSuccess"));
const BrandElements = lazy(() => import("./pages/blog/BrandElements"));
const BrandLoyalty = lazy(() => import("./pages/blog/BrandLoyalty"));
const BrandSecrets = lazy(() => import("./pages/blog/BrandSecrets"));
const BrandGuide = lazy(() => import("./pages/blog/BrandGuide"));

// Dashboard (team only)
const RequireRole = lazy(() => import("./components/dashboard/RequireRole"));
const DashboardLayout = lazy(() => import("./components/dashboard/DashboardLayout"));
const DashboardLogin = lazy(() => import("./pages/DashboardLogin"));
const DashboardResetPassword = lazy(() => import("./pages/DashboardResetPassword"));
const DashboardOverview = lazy(() => import("./pages/DashboardOverview"));
const DashboardAllSubmissions = lazy(() => import("./pages/DashboardSubmissions").then((m) => ({ default: m.DashboardAllSubmissions })));
const DashboardLeads = lazy(() => import("./pages/DashboardSubmissions").then((m) => ({ default: m.DashboardLeads })));
const DashboardCreators = lazy(() => import("./pages/DashboardSubmissions").then((m) => ({ default: m.DashboardCreators })));
const DashboardTeam = lazy(() => import("./pages/DashboardTeam"));
const DashboardSettings = lazy(() => import("./pages/DashboardSettings"));
const DashboardAuditLog = lazy(() => import("./pages/DashboardAuditLog"));
const DashboardHelp = lazy(() => import("./pages/DashboardHelp"));
const AnalyticsOverview = lazy(() => import("./pages/analytics/AnalyticsOverview"));
const AnalyticsPages = lazy(() => import("./pages/analytics/AnalyticsPages"));
const AnalyticsVisitors = lazy(() => import("./pages/analytics/AnalyticsVisitors"));
const AnalyticsSources = lazy(() => import("./pages/analytics/AnalyticsSources"));
const AnalyticsTracker = lazy(() => import("@/components/AnalyticsTracker"));



const queryClient = new QueryClient();

const PageFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <ScrollToTop />
      <LanguageProvider>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <AuthDialog />
            {/* <HomeLeadMagnet /> */}
            <Suspense fallback={<PageFallback />}>
              <AnalyticsTracker />
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/pro" element={<DBPro />} />
                <Route path="/consultation" element={<Consultation />} />
                <Route path="/boot_camp" element={<Bootcamp />} />
                <Route path="/bootcamp-registration" element={<BootcampRegistration />} />
                <Route path="/bootcamp-waitlist" element={<BootcampWaitlist />} />
                <Route path="/bootcamp-success" element={<BootcampSuccess />} />
                <Route path="/services" element={<Services />} />
                <Route path="/case-studies" element={<Portfolio />} />
                <Route path="/case-studies/:id" element={<CaseStudyDetail />} />
                <Route path="/people/khalid" element={<KhalidProfile />} />
                <Route path="/wireframe" element={<Wireframe />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/dbpro-success" element={<DBProSuccess />} />
                <Route path="/payment-cancelled" element={<PaymentCancelled />} />
                <Route path="/booking-success" element={<BookingSuccess />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/:slug" element={<ShopProduct />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/brand-guide" element={<BrandGuide />} />
                <Route path="/blog/brand-elements" element={<BrandElements />} />

                <Route path="/blog/brand-loyalty" element={<BrandLoyalty />} />
                <Route path="/blog/brand-secrets" element={<BrandSecrets />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/team" element={<Team />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/advertise" element={<Advertise />} />
                <Route path="/events" element={<Events />} />
                <Route path="/vendors" element={<Vendors />} />
                <Route path="/testimonial" element={<Testimonial />} />
                <Route path="/testimonial-success" element={<TestimonialSuccess />} />
                <Route path="/influencer-campaign" element={<InfluencerCampaign />} />
                <Route path="/influencers" element={<Influencers />} />
                <Route path="/influencer-network" element={<InfluencerNetwork />} />
                <Route path="/work-with-us" element={<WorkWithUs />} />

                <Route path="/work-with-us-success" element={<WorkWithUsSuccess />} />

                {/* Team dashboard */}
                <Route path="/dashboard/login" element={<DashboardLogin />} />
                <Route path="/dashboard/reset-password" element={<DashboardResetPassword />} />
                <Route
                  path="/dashboard"
                  element={
                    <RequireRole>
                      <DashboardLayout />
                    </RequireRole>
                  }
                >
                  <Route index element={<DashboardOverview />} />
                  <Route path="submissions" element={<DashboardAllSubmissions />} />
                  <Route path="leads" element={<DashboardLeads />} />
                  <Route path="creators" element={<DashboardCreators />} />
                  <Route path="settings" element={<DashboardSettings />} />
                  <Route path="help" element={<DashboardHelp />} />
                  <Route path="team" element={<RequireRole adminOnly><DashboardTeam /></RequireRole>} />
                  <Route path="audit" element={<RequireRole adminOnly><DashboardAuditLog /></RequireRole>} />
                  <Route path="analytics" element={<AnalyticsOverview />} />
                  <Route path="analytics/pages" element={<AnalyticsPages />} />
                  <Route path="analytics/visitors" element={<AnalyticsVisitors />} />
                  <Route path="analytics/sources" element={<AnalyticsSources />} />
                </Route>
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                {/* Legacy URLs from the old website, redirected so Google stops reporting Soft 404 */}
                <Route path="/marketing-bootcamp" element={<Navigate to="/boot_camp" replace />} />
                <Route path="/bootcamp" element={<Navigate to="/boot_camp" replace />} />
                <Route path="/copy-of-masterclass" element={<Navigate to="/boot_camp" replace />} />
                <Route path="/ugc-creators" element={<Navigate to="/influencers" replace />} />
                <Route path="/services-3" element={<Navigate to="/services" replace />} />
                <Route path="/services-4" element={<Navigate to="/services" replace />} />
                <Route path="/event-list" element={<Navigate to="/events" replace />} />
                <Route path="/db-pro" element={<Navigate to="/pro" replace />} />
                <Route path="/about-1" element={<Navigate to="/team" replace />} />
                <Route path="/about-8" element={<Navigate to="/team" replace />} />
                <Route path="*" element={<NotFound />} />

              </Routes>
            </Suspense>
          </TooltipProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
